import { addDays, differenceInDays, isBefore, startOfDay, subDays } from 'date-fns';

import { dateFromStr } from '@utils';

export const getLinesData = chartData => {
  const linesData = [];

  Object.entries(chartData).forEach(([metric, values]) => {
    values.forEach((item, i) => {
      const [timestamp, value] = item;
      if (!linesData[i]) linesData[i] = { date: timestamp };
      linesData[i][metric] = value;
    });
  });
  return linesData;
};

const SESSIONS_DATA_DELAY_DAYS = 3;

export const getCampaignPeriods = (selectedCampaign, windowSize) => {
  if (!selectedCampaign) return {};
  const today = startOfDay(new Date());
  const campaignStart = startOfDay(new Date(selectedCampaign.start));
  const campaignEnd = startOfDay(new Date(selectedCampaign.end));
  let preStart = subDays(campaignStart, windowSize);
  let preEnd = subDays(campaignStart, 1);
  let postStart = addDays(campaignEnd, 1);
  let postEnd = addDays(campaignEnd, windowSize);

  const safePostEnd = subDays(today, SESSIONS_DATA_DELAY_DAYS);
  let campaignIsOngoing = false;

  if (isBefore(safePostEnd, campaignEnd)) {
    postEnd = safePostEnd;
    postStart = subDays(postEnd, windowSize);
    campaignIsOngoing = true;
  }

  if (isBefore(postStart, preEnd)) {
    preEnd = subDays(postStart, 1);
    preStart = subDays(preEnd, windowSize);
  }

  return {
    preStart,
    preEnd,
    campaignStart,
    campaignEnd,
    postStart,
    postEnd,
    campaignIsOngoing
  };
};

export const getMetricStats = (
  selectedCampaign,
  linesData,
  metricKey,
  windowSize,
  decayThreshold
) => {
  if (!selectedCampaign)
    return {
      avgPre: 0,
      avgDuring: 0,
      avgPost: 0,
      peakDate: null,
      peakValue: null,
      daysToPeak: null,
      upliftLastDay: null,
      daysToUplift: null
    };
  const preValues = [];
  const duringValues = [];
  const postValues = [];

  const {
    campaignStart,
    campaignEnd,
    preStart,
    preEnd,
    postStart,
    postEnd,
    campaignIsOngoing
  } = getCampaignPeriods(selectedCampaign, windowSize);

  let peakDate = null;
  let peakValue = null;

  linesData.forEach(item => {
    const itemDate = startOfDay(new Date(item.date));
    const itemValue = item[metricKey] || 0;
    if (itemDate >= preStart && itemDate <= preEnd) {
      preValues.push(itemValue);
    } else if (itemDate >= campaignStart && itemDate <= campaignEnd) {
      duringValues.push(itemValue);
      if (!peakDate || itemValue > peakValue) {
        peakValue = itemValue;
        peakDate = itemDate;
      }
    }
    if (itemDate >= postStart && itemDate <= postEnd) {
      postValues.push(itemValue);
    }
  });

  const avgPre = preValues.length ? preValues.reduce((a, b) => a + b, 0) / preValues.length : 0;
  const avgDuring = duringValues.length
    ? duringValues.reduce((a, b) => a + b, 0) / duringValues.length
    : 0;
  const avgPost = postValues.length ? postValues.reduce((a, b) => a + b, 0) / postValues.length : 0;
  const daysToPeak = differenceInDays(peakDate, campaignStart);

  const minValueToDetectUplift = avgPre * (1 + decayThreshold / 100);

  let upliftLastDay = null;

  linesData.forEach(item => {
    const itemDate = startOfDay(new Date(item.date));
    if (itemDate < postStart || upliftLastDay) return;
    const itemValue = item[metricKey] || 0;
    if (itemValue < minValueToDetectUplift) {
      upliftLastDay = itemDate;
    }
  });

  const daysToUplift = upliftLastDay ? differenceInDays(upliftLastDay, postStart) : null;

  const campaignAmountSpent = selectedCampaign.amount_spent || 0;

  const preValuesSum = preValues.reduce((a, b) => a + b, 0);
  const postValuesSum = postValues.reduce((a, b) => a + b, 0);

  const diffSum = postValuesSum - preValuesSum;
  const roi = campaignAmountSpent
    ? ((diffSum - campaignAmountSpent) / campaignAmountSpent) * 100
    : 0;

  const roas = campaignAmountSpent ? (postValuesSum / campaignAmountSpent) * 100 : 0;

  return {
    avgPre,
    avgDuring,
    avgPost,
    peakDate,
    peakValue,
    daysToPeak,
    upliftLastDay,
    daysToUplift,
    roi,
    roas,
    campaignIsOngoing
  };
};

export const getCampaignStats = (
  campaign,
  linesData,
  windowSize,
  decayThreshold,
  apiSelectedMetrics
) => {
  const stats = apiSelectedMetrics.reduce((acc, metric) => {
    acc[metric] = getMetricStats(campaign, linesData, metric, windowSize, decayThreshold);
    return acc;
  }, {});
  return stats;
};
export const getCampaignExtendedRange = (campaign, extensionDays = 90) => {
  const end = addDays(dateFromStr(campaign.end), extensionDays);
  const now = new Date();
  const safeEnd = isBefore(end, now) ? end : now;
  return { from: subDays(dateFromStr(campaign.start), extensionDays), to: safeEnd };
};
